import { markRaw } from 'vue';
import DOCIcon from '@/assets/icons/shared/files/doc.svg?component';
import XLSIcon from '@/assets/icons/shared/files/xls.svg?component';
import JPGIcon from '@/assets/icons/shared/files/jpg.svg?component';
import ODSIcon from '@/assets/icons/shared/files/ods.svg?component';
import ODTIcon from '@/assets/icons/shared/files/odt.svg?component';
import PDFIcon from '@/assets/icons/shared/files/pdf.svg?component';
import PNGIcon from '@/assets/icons/shared/files/png.svg?component';
import RTFIcon from '@/assets/icons/shared/files/rtf.svg?component';
import UnknownIcon from '@/assets/icons/shared/files/unknown.svg?component';
import {
  ALL_FILE_TYPES,
  EDITABLE_FILE_TYPES,
  PREVIEW_FILE_TYPES,
  SERVER_PREVIEW_FILE_TYPES,
} from '@/modules/common/consts/file.ts';
import type { IconComponent } from '@/modules/common/types/icon.type.ts';

export interface IBaseFile {
  id: string | number;
  name: string;
  size?: number;
  title: string;
  extension: string;
  type: string;
  formattedTitle: string;
  readonly icon: IconComponent;
  canEdit: boolean;
  canPreview: boolean;
  canServerPreview: boolean;
  previewLoaded: boolean;
  renamed: boolean;
  commented: boolean;
  readonly modified: boolean;
  comment?: string | null;
  systemPrefix: string;
  prefixAutoStripped: boolean;
  rename(name: string): void;
  restoreSystemPrefix(): void;
  setComment(comment: string): void;
}

export default abstract class BaseFile implements IBaseFile {
  id: string | number;
  name: string;
  size?: number;
  title: string;
  extension: string;
  type: string;
  formattedTitle: string;
  canEdit: boolean;
  canPreview: boolean;
  canServerPreview: boolean;
  previewLoaded: boolean = false;
  renamed: boolean = false;
  commented: boolean = false;
  comment?: string | null;
  systemPrefix: string = '';
  prefixAutoStripped: boolean = false;

  get modified(): boolean {
    return this.renamed || this.commented;
  }

  get icon(): IconComponent {
    return markRaw(this.getIcon(this.type));
  }

  protected constructor(id: string | number, name: string, size?: number) {
    this.id = id;
    this.name = name;
    this.size = size;
    this.title = this.getTitle(name);
    this.extension = this.getExtension(name);
    this.type = this.extension.toLowerCase();
    this.formattedTitle = this.getFormattedTitle(this.type, this.title, name);
    this.canEdit = this.getCanEdit(this.type);
    this.canPreview = this.getCanPreview(this.type);
    this.canServerPreview = this.getCanServerPreview(this.type);
  }

  public rename(name: string) {
    this.applyName(name);
    this.renamed = true;
    this.prefixAutoStripped = false;
  }

  public restoreSystemPrefix() {
    if (!this.prefixAutoStripped) {
      return;
    }

    this.applyName(this.systemPrefix + this.name);
    this.prefixAutoStripped = false;
    this.renamed = false;
  }

  protected markPrefixStripped(prefix: string) {
    this.systemPrefix = prefix;
    this.prefixAutoStripped = !!prefix;
    this.renamed = !!prefix;
  }

  protected applyName(name: string) {
    this.name = name;
    this.title = this.getTitle(name);
    this.extension = this.getExtension(name);
    this.type = this.extension.toLowerCase();
    this.formattedTitle = this.getFormattedTitle(this.type, this.title, name);
  }

  public setComment(comment: string) {
    this.comment = comment;
    this.commented = !!comment;
  }

  private getTitle(name: string) {
    return name.split('.').slice(0, -1).join('.');
  }

  private getExtension(name: string) {
    return name.split('.').pop() || '';
  }

  private getFormattedTitle(type: string, title: string, name: string) {
    return ALL_FILE_TYPES.includes(type) ? title : name;
  }

  private getIcon(type: string) {
    switch (type) {
      case 'doc':
      case 'docx':
        return DOCIcon;
      case 'xls':
      case 'xlsm':
      case 'xlsx':
      case 'xltx':
      case 'xlsb':
      case 'xlam':
        return XLSIcon;
      case 'jpg':
      case 'jpeg':
        return JPGIcon;
      case 'ods':
        return ODSIcon;
      case 'odt':
        return ODTIcon;
      case 'pdf':
        return PDFIcon;
      case 'png':
        return PNGIcon;
      case 'rtf':
        return RTFIcon;
      default:
        return UnknownIcon;
    }
  }

  private getCanEdit(type: string) {
    return EDITABLE_FILE_TYPES.includes(type);
  }

  private getCanPreview(type: string) {
    return PREVIEW_FILE_TYPES.includes(type);
  }

  private getCanServerPreview(type: string) {
    return SERVER_PREVIEW_FILE_TYPES.includes(type);
  }

  static async toBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Error converting Blob to Base64'));
        } else {
          resolve(result.split('base64,')[1]);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  }

  public setPreviewLoaded(loaded: boolean) {
    this.previewLoaded = loaded;
  }
}
